import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { IMAGES } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";
import OfferForm from "@/components/OfferForm";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Aerial view of open plotted land with highway connectivity near Nagpur, Maharashtra"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/25" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-navy to-transparent" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-40 pt-44 sm:px-6 lg:px-8">
        <motion.p
          {...fade(1.0)}
          className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold sm:text-xs"
        >
          <span className="h-px w-10 bg-gold" aria-hidden="true" />
          Residential &bull; Commercial &bull; Investment Properties
        </motion.p>

        <h1 className="mt-7 font-display text-5xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              Invest in Land.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              Build Your <span className="italic text-gold">Future.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p {...fade(0.85)} className="mt-6 max-w-xl text-base text-white/75 md:text-lg">
          Premium residential and commercial plots in strategically connected locations around Nagpur.
        </motion.p>

        <motion.div {...fade(1.0)} className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
          {["Near Samruddhi Mahamarg", "Umred\u2013Wardha Road", "Nagpur"].map((loc) => (
            <span key={loc} className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-gold" aria-hidden="true" />
              {loc}
            </span>
          ))}
        </motion.div>

        <motion.div {...fade(1.15)} className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/properties"
            data-testid="hero-explore-properties-button"
            className="rounded-sm bg-gold px-8 py-4 text-sm font-bold uppercase tracking-wider text-navy transition-colors duration-200 hover:bg-gold-light"
          >
            Explore Properties
          </Link>
          <button
            data-testid="hero-contact-button"
            onClick={() => scrollToId("contact")}
            className="rounded-sm border border-white/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-gold hover:text-gold"
          >
            Contact Us
          </button>
        </motion.div>

        <motion.div {...fade(1.25)} className="mt-8 max-w-2xl" aria-hidden="true">
          <div className="overflow-hidden border-y border-gold/30 py-2">
            <div className="flex w-max animate-marquee items-center">
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className="flex items-center whitespace-nowrap">
                  <span className="px-6 text-xs font-extrabold uppercase tracking-[0.3em] text-gold">
                    Special Offer
                  </span>
                  <span className="text-[10px] text-gold/60">&#9670;</span>
                  <span className="px-6 text-xs font-extrabold uppercase tracking-[0.3em] text-white/70">
                    Free Site Visit
                  </span>
                  <span className="text-[10px] text-gold/60">&#9670;</span>
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div {...fade(1.4)} className="mt-6 max-w-xl">
          <OfferForm />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="h-10 w-px bg-gradient-to-b from-transparent via-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
